/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { formatBytes } from "../format";

/**
 * Fixed panel listing in-flight and finished uploads. Stays mounted while the
 * user keeps browsing, so navigating between folders mid-upload is safe.
 */
const UploadQueue = ({ uploads, onCancel, onDismiss }) => {
  if (uploads.length === 0) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: 340,
        maxHeight: 320,
        overflowY: "auto",
        zIndex: 1300,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1 }}>
        <Typography variant="subtitle2">
          Uploads ({uploads.filter((u) => u.status === "uploading").length} active)
        </Typography>
        <IconButton size="small" onClick={onDismiss} aria-label="Dismiss finished uploads">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Stack sx={{ px: 2, pb: 2 }} spacing={1.5}>
        {uploads.map((upload) => (
          <Box key={upload.id}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {upload.status === "done" && <CheckCircleIcon color="success" fontSize="small" />}
              {upload.status === "error" && (
                <Tooltip title={upload.error || "Upload failed"}>
                  <ErrorIcon color="error" fontSize="small" />
                </Tooltip>
              )}
              <Typography variant="body2" noWrap sx={{ flex: 1 }} title={upload.name}>
                {upload.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatBytes(upload.size)}
              </Typography>
              {upload.status === "uploading" && (
                <IconButton size="small" onClick={() => onCancel(upload.id)} aria-label="Cancel upload">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            {upload.status === "uploading" && (
              <LinearProgress
                variant="determinate"
                value={upload.progress}
                sx={{ mt: 0.5, "& .MuiLinearProgress-bar": { backgroundColor: "accent.main" } }}
              />
            )}
            {upload.status === "error" && (
              <Typography variant="caption" color="error">{upload.error}</Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default UploadQueue;
